import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Eye, EyeOff, ArrowLeft, RefreshCw, Mail } from 'lucide-react';
import { goHome, goToSignup, goToAdmin, goToCommunity, goToCommunityAdmin, goToVendorAdmin, goToForgotPassword } from '../utils/navigation';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { login, loading, error, clearError } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear validation errors when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await login(formData.phone, formData.password);
      const storedUser = sessionStorage.getItem('user');
      let role: string | undefined;
      if (storedUser) {
        try {
          role = JSON.parse(storedUser).role;
        } catch {}
      }

      if (role === 'ADMIN') {
        goToAdmin();
      } else if (role === 'COMMITTEE_ADMIN') {
        goToCommunityAdmin();
      } else if (role === 'VENDOR_ADMIN') {
        goToVendorAdmin();
      } else if (role && ['COMMITTEE', 'VENDOR'].includes(role)) {
        goToCommunity();
      } else {
        goHome();
      }
    } catch (err) {
      // Error is handled by the auth context
    }
  };

  const goBack = () => {
    goHome();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dpn-pink/10 via-white to-dpn-olive/10 flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={goBack}
            className="absolute top-8 left-8 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-20 w-20 object-contain"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          </div>
          <h1 className="text-3xl font-lucy-rose font-bold text-gray-800 mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-600">
            Sign in to your Durga Puja Network account
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Phone Number or Email */}
          <div>
            <label htmlFor="phone" className="block text-sm font-lucy-rose font-medium text-gray-700 mb-2">
              Phone Number or Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent transition-all duration-200 ${
                  validationErrors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter your phone number or email"
              />
            </div>
            {validationErrors.phone && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-lucy-rose font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent transition-all duration-200 ${
                  validationErrors.password ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {validationErrors.password && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
            )}
          </div>

          {/* Forgot Password Link */}
         
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-dpn-pink hover:bg-dpn-raspberry disabled:bg-gray-400 text-white font-lucy-rose font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span>Signing In...</span>
              </div>
            ) : (
              'Sign In'
            )}
          </button>
          <div className="text-center mb-4">
            <button
              type="button"
              onClick={() => goToForgotPassword()}
              className="text-dpn-pink hover:text-dpn-raspberry font-medium transition-colors font-lucy-rose text-sm"
            >
              Forgot your password?
            </button>
          </div>
        </form>

        {/* Signup Link */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <button
              onClick={goToSignup}
              className="text-dpn-pink hover:text-dpn-raspberry font-lucy-rose font-medium transition-colors duration-200"
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
